import { useEffect, useState } from "react";
import type { PairingCode } from "../api";
import { useAuth } from "../auth";
import { ErrorNote, useLoad } from "../ui";

const date = (s: string) => new Date(s).toLocaleDateString("nl-NL");

/**
 * Link an e-reader (Kobo with the KOReader plugin): the app shows a short code, the reader
 * types it on the e-reader. The device can only read; unlink it here at any time.
 */
export function Devices() {
  const { api } = useAuth();
  const devices = useLoad(() => api.devices(), [api]);
  const [code, setCode] = useState<PairingCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  // Count down while a code is shown; once it is used, the new device appears in the list.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => {
      setNow(Date.now());
      devices.reload();
    }, 5000);
    return () => clearInterval(t);
  }, [code, devices.reload]);

  const count = devices.data?.length ?? 0;
  useEffect(() => setCode(null), [count]);

  const left = code ? Math.max(0, Math.round((Date.parse(code.expires_at) - now) / 60000)) : 0;
  const expired = code !== null && Date.parse(code.expires_at) <= now;

  async function newCode() {
    setError(null);
    try {
      setCode(await api.pairingCode());
      setNow(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function unlink(id: string, name: string) {
    if (!window.confirm(`${name} ontkoppelen? Het apparaat kan daarna niets meer opzoeken.`)) return;
    try {
      await api.unlinkDevice(id);
      devices.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <details className="card account">
      <summary>
        <strong>E-reader koppelen</strong>
        {count > 0 && <span className="muted small"> · {count} gekoppeld</span>}
      </summary>
      <div className="stack" style={{ marginTop: "0.75rem" }}>
        <p className="small">
          Zoek personages en plaatsen op vanuit je Kobo (met KOReader en de FabulaWiki-plugin),
          spoilervrij tot het hoofdstuk dat je leest. Kies op de e-reader{" "}
          <em>FabulaWiki → Inloggen</em> en typ daar deze code.
        </p>
        {code && !expired ? (
          <div className="note" role="status">
            <span className="pairing-code">{code.code}</span>
            <p className="small muted">
              Nog {left <= 1 ? "1 minuut" : `${left} minuten`} geldig, één keer te gebruiken.
            </p>
          </div>
        ) : (
          <div className="row">
            <button className="btn" onClick={() => void newCode()}>
              {expired ? "Nieuwe code maken" : "Koppelcode maken"}
            </button>
            {expired && <span className="muted small">De code is verlopen.</span>}
          </div>
        )}
        {count > 0 && (
          <ul className="device-list">
            {devices.data!.map((d) => (
              <li key={d.id} className="row">
                <span>
                  <strong>{d.name}</strong>
                  <span className="muted small">
                    {" "}
                    · gekoppeld {date(d.created_at)}
                    {d.last_used_at ? ` · laatst gebruikt ${date(d.last_used_at)}` : ""}
                  </span>
                </span>
                <button className="btn btn-ghost small" onClick={() => void unlink(d.id, d.name)}>
                  Ontkoppelen
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="small muted">
          Een gekoppeld apparaat kan alleen lezen: geen boeken toevoegen, delen, bewerken of je
          account wijzigen.
        </p>
        <ErrorNote error={error ?? devices.error} />
      </div>
    </details>
  );
}
